export const CONTENT_PAGE_KEYS = [
  "aboutUs", "makeYourFstep", "becomeRoyalty", "becomeSeller", "LinkcustomerShop",
  "editProduct", "admAssPage", "becomeProvider", "termConditProdSel", "catalogProdSel", "infoProdSel",
  "filterProdSel", "imageProdSel", "planUserRoyal", "termConditionRoyal", "royalAgree", "planUserSeller",
  "termConditionSeller", "sellerAgree", "planUserProvider", "termConditionProvider", "providerAgree",
  "planAffaireElakay", "termConditionAssociate", "associateAgree", "planProInvestElakay",
  "termConditionInvest", "investmentAgree", "planProAdminElakay", "termConditionAdmin",
  "AdministratorAgree", "planProFamillyElakay", "termConditionFamilly", "FamillyAgree",
];

export const CONTENT_KEYS = CONTENT_PAGE_KEYS;

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const isPageBuilderDoc = (value) =>
  value && typeof value === "object" && Array.isArray(value.pages);

const resolvePageBuilderDoc = (...candidates) => {
  for (const candidate of candidates) {
    const doc =
      candidate?.pageBuilderDoc ||
      candidate?.pageBuilder?.doc ||
      candidate?.doc ||
      candidate;
    if (isPageBuilderDoc(doc)) return doc;
  }
  return null;
};

const toTextArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return value.split(/\r?\n/).filter(Boolean);
  if (typeof value === "number" || typeof value === "boolean") return [String(value)];
  return [];
};

const toMediaPath = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const normalizeSectionMedia = (section) => {
  const imagePath = toMediaPath(section.imagePath || section.image?.src || section.image || section.thumbnail || "");
  const videoPath = toMediaPath(section.videoPath || section.video?.src || section.video || "");
  const rawVori = toMediaPath(section.VORI).toLowerCase();
  let VORI = rawVori === "image" || rawVori === "video" ? rawVori : "";

  if (VORI === "image" && !imagePath && videoPath) VORI = "video";
  if (VORI === "video" && !videoPath && imagePath) VORI = "image";
  if (!VORI) VORI = videoPath ? "video" : imagePath ? "image" : "";
  if (!imagePath && !videoPath) VORI = "";

  return { imagePath, videoPath, VORI };
};

const getLanguageDocumentId = (languageData, fallback = "") => {
  const raw = languageData?._id || languageData?.id || languageData?.language?._id || fallback;
  return raw ? String(raw) : "";
};

export const getContentPageId = (languageData, pageKey, fallbackSourceId = "") => {
  const sourceId = getLanguageDocumentId(languageData, fallbackSourceId);
  const explicitId =
    languageData?.contentPageIds?.[pageKey] ||
    languageData?.contentPages?.[pageKey]?._id;
  if (explicitId) return String(explicitId);
  return sourceId ? `${sourceId}:${pageKey}` : pageKey;
};

export const normalizeContentPageSections = (value, pageKey, pageId = pageKey) => {
  if (!value) return [];

  const paragraphLikeKeys = [
    "title", "text", "list", "VORI", "imagePath", "videoPath",
    "stylePara", "link", "href", "image", "video", "thumbnail",
  ];

  let rawSections = [];
  if (Array.isArray(value)) {
    rawSections = value;
  } else if (isPlainObject(value) && Array.isArray(value.sections)) {
    rawSections = value.sections;
  } else if (isPlainObject(value) && paragraphLikeKeys.some((key) => hasOwn(value, key))) {
    rawSections = [value];
  } else if (isPlainObject(value)) {
    rawSections = Object.values(value).flatMap((entry) => {
      if (Array.isArray(entry)) return entry;
      return isPlainObject(entry) ? [entry] : [];
    });
  }

  return rawSections
    .filter((section) => isPlainObject(section))
    .map((section, index) => {
      const media = normalizeSectionMedia(section);
      return {
        _id: section._id || section.id || `${pageId}-section-${index + 1}`,
        title: section.title || "",
        text: toTextArray(section.text),
        list: toTextArray(section.list),
        stylePara: Array.isArray(section.stylePara) ? section.stylePara : [4, "a"],
        VORI: media.VORI,
        imagePath: media.imagePath,
        videoPath: media.videoPath,
        href: isPlainObject(section.href) ? section.href : { title: "", href: "" },
        link: isPlainObject(section.link) ? section.link : { title: "", href: "" },
      };
    });
};

export const buildContentPageData = (pageKey, rawValue, options = {}) => {
  if (!pageKey) return null;
  const {
    label,
    languageData = null,
    sourceId = "",
    defaultRenderer = "LanguagePage",
  } = options;
  const pageId = getContentPageId(languageData, pageKey, sourceId);
  const sections = normalizeContentPageSections(rawValue, pageKey, pageId);
  if (!sections.length) return null;
  const pageBuilderDoc = resolvePageBuilderDoc(
    rawValue,
    languageData?.contentPages?.[pageKey],
    languageData?.contentPageDocs?.[pageKey]
  );

  const contentPage = {
    _id: pageId,
    pageId,
    pageKey,
    sourceKey: pageKey,
    sourceLanguageId: getLanguageDocumentId(languageData, sourceId),
    pageTitle: label || languageData?.contentPages?.[pageKey]?.pageTitle || pageKey,
    defaultRenderer,
    sections,
  };
  if (pageBuilderDoc) contentPage.pageBuilderDoc = pageBuilderDoc;
  return contentPage;
};

export const buildContentPageDataset = (pageKey, rawValue, options = {}) => {
  const data = buildContentPageData(pageKey, rawValue, options);
  if (!data) return null;
  return {
    label: data.pageTitle,
    pageMode: "contentPage",
    data,
    isLive: Boolean(options.isLive),
  };
};

export const resolveLanguageContentValue = (languageAPI, pageKey) => {
  if (!languageAPI || !pageKey) return null;
  const apiData = languageAPI.elakayInfoAPI || {};
  const fallbackData = languageAPI.elakayInfo || {};
  if (typeof languageAPI.checkLangObjDefault === "function") {
    return languageAPI.checkLangObjDefault(apiData, fallbackData, pageKey);
  }
  return apiData?.[pageKey] || fallbackData?.[pageKey] || null;
};

export const getLanguageAPIContentSourceId = (languageAPI) =>
  getLanguageDocumentId(languageAPI?.elakayInfoAPI) ||
  getLanguageDocumentId(languageAPI?.elakayInfo) ||
  "";

export const buildContentPageDataFromLanguageAPI = (languageAPI, pageKey, options = {}) => {
  const rawValue = resolveLanguageContentValue(languageAPI, pageKey);
  return buildContentPageData(pageKey, rawValue, {
    ...options,
    languageData: languageAPI?.elakayInfoAPI || languageAPI?.elakayInfo || null,
    sourceId: getLanguageAPIContentSourceId(languageAPI),
  });
};

export const buildLanguageContentPageDatasets = (languageAPI, options = {}) => {
  const { menuLabels = {}, includeEmpty = false } = options;
  const datasets = {};

  CONTENT_PAGE_KEYS.forEach((pageKey) => {
    const rawValue = resolveLanguageContentValue(languageAPI, pageKey);
    const dataset = buildContentPageDataset(pageKey, rawValue, {
      label: menuLabels[pageKey] || pageKey,
      languageData: languageAPI?.elakayInfoAPI || languageAPI?.elakayInfo || null,
      sourceId: getLanguageAPIContentSourceId(languageAPI),
      isLive: true,
    });

    if (dataset) {
      datasets[`cp_${pageKey}`] = dataset;
    } else if (includeEmpty) {
      const pageId = getContentPageId(languageAPI?.elakayInfoAPI || languageAPI?.elakayInfo, pageKey, getLanguageAPIContentSourceId(languageAPI));
      datasets[`cp_${pageKey}`] = {
        label: menuLabels[pageKey] || pageKey,
        pageMode: "contentPage",
        data: {
          _id: pageId,
          pageId,
          pageKey,
          sourceKey: pageKey,
          sourceLanguageId: getLanguageAPIContentSourceId(languageAPI),
          pageTitle: menuLabels[pageKey] || pageKey,
          defaultRenderer: "LanguagePage",
          sections: [],
        },
        isLive: true,
      };
    }
  });

  return datasets;
};

export const getContentPageBuilderDoc = (contentPageData) => {
  return resolvePageBuilderDoc(contentPageData);
};
